import React, { useCallback, useEffect, useRef, useState } from "react";
import { ISizeInfo } from "./types";
import { useLazyListContext } from "./LazyListContext";

interface LazyItemProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item: any;
  index: number;
  children: React.ReactNode;
  unstableContent?: string;
}

const LazyItem: React.FC<LazyItemProps> = ({ item, index, children, unstableContent }) => {
  const { state, dispatch } = useLazyListContext();
  const [height, setHeight] = useState(0);
  const [hasFocus, setHasFocus] = useState(false);
  const { visibleIndices } = state;

  const onSizeChange = useCallback(
    (index: number, size: ISizeInfo) => {
      dispatch({ type: "updateItemPosition", payload: { index, size } });
    },
    [dispatch]
  );

  const itemRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(true);
  const [inited, setInited] = useState(false);

  useEffect(() => {
    setIsVisible(visibleIndices.has(index));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleIndices.has(index)]);

  useEffect(() => {
    const item = itemRef.current;
    if (!item) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isVisible = entry.isIntersecting;
          setIsVisible(isVisible);
        });
      },
      {
        root: item?.parentElement?.parentElement,
        rootMargin: "400px", // 在视口周围添加 400px 的边距
        threshold: 0.01,
      }
    );

    observer.observe(item);

    return () => {
      observer.unobserve(item);
    };
  }, []);

  const setItemHeight = (target: Element) => {
    const style = window.getComputedStyle(target);
    const margin = parseFloat(style.marginTop) + parseFloat(style.marginBottom);
    const itemHeight = (target as HTMLElement).offsetHeight + margin;
    if (!itemHeight || height === itemHeight) {
      return;
    }
    if (!inited) {
      setInited(true);
    }
    setHeight(itemHeight);
    onSizeChange(index, { height: itemHeight });
  };

  useEffect(() => {
    const target = itemRef.current?.children[0];
    const resizeObserver = new ResizeObserver((entries) => {
      setItemHeight(entries[0].target);
    });

    if (target) {
      resizeObserver.observe(target);
    }

    return () => {
      if (target) {
        resizeObserver.unobserve(target);
      }
    };
  }, []);

  useEffect(() => {
    const target = itemRef.current?.children[0];
    if (target) {
      setItemHeight(target);
    }
  }, [item, unstableContent]);

  return (
    <div ref={itemRef} style={!isVisible || !hasFocus ? { height } : undefined} onFocus={()=>setHasFocus(true)} onBlur={()=>setHasFocus(false)}>
      {isVisible || !inited ? children : null}
    </div>
  );
};

export default LazyItem;
